import { useMemo } from 'react';
import { useChamadas } from './useChamadas';

export type Tendencia = 'MELHORANDO' | 'ESTAVEL' | 'PIORANDO';

interface TendenciaResult {
  tendencia: Tendencia;
  freqAtual: number;   // % frequencia ultimas 2 semanas
  freqAnterior: number; // % frequencia 2 semanas antes
  totalAulas: number;
  isLoading: boolean;
}

/**
 * Hook que calcula a tendencia de frequencia de um aluno
 * comparando as ultimas 2 semanas com as 2 semanas anteriores.
 *
 * @param alunoId - ID do aluno
 * @param turmaId - ID da turma (filtro opcional)
 */
export function useTendenciaAluno(alunoId?: string, turmaId?: string): TendenciaResult {
  const hoje = new Date();

  // Periodo recente: ultimas 2 semanas
  const fimRecente = hoje.toISOString().split('T')[0];
  const inicioRecente = new Date(hoje);
  inicioRecente.setDate(inicioRecente.getDate() - 14);
  const inicioRecenteStr = inicioRecente.toISOString().split('T')[0];

  // Periodo anterior: 2-4 semanas atras
  const fimAnterior = new Date(inicioRecente);
  fimAnterior.setDate(fimAnterior.getDate() - 1);
  const fimAnteriorStr = fimAnterior.toISOString().split('T')[0];
  const inicioAnterior = new Date(fimAnterior);
  inicioAnterior.setDate(inicioAnterior.getDate() - 14);
  const inicioAnteriorStr = inicioAnterior.toISOString().split('T')[0];

  // Buscar chamadas dos 2 periodos
  const { chamadas: chamadasRecente, isLoading: l1 } = useChamadas({
    data: undefined,
    turmaId,
  });

  // Calcular frequencia por periodo
  const { freqAtual, freqAnterior, totalAulas, tendencia } = useMemo(() => {
    if (!alunoId || !chamadasRecente.length) {
      return { freqAtual: 100, freqAnterior: 100, totalAulas: 0, tendencia: 'ESTAVEL' as Tendencia };
    }

    let presenteRecente = 0, totalRecente = 0;
    let presenteAnterior = 0, totalAnterior = 0;

    chamadasRecente.forEach((chamada: any) => {
      const dataRef = chamada.dataReferencia?.split('T')[0];
      if (!dataRef) return;

      // Verificar se esta chamada tem dados deste aluno
      const alunoChamada = chamada.alunos?.find((a: any) => a.alunoId === alunoId);
      if (!alunoChamada) return;

      const isPresente = alunoChamada.statusPresenca === 'PRESENTE' || alunoChamada.statusPresenca === 'ATRASADO';

      if (dataRef >= inicioRecenteStr && dataRef <= fimRecente) {
        totalRecente++;
        if (isPresente) presenteRecente++;
      } else if (dataRef >= inicioAnteriorStr && dataRef <= fimAnteriorStr) {
        totalAnterior++;
        if (isPresente) presenteAnterior++;
      }
    });

    const fAtual = totalRecente > 0 ? Math.round((presenteRecente / totalRecente) * 100) : 100;
    const fAnterior = totalAnterior > 0 ? Math.round((presenteAnterior / totalAnterior) * 100) : 100;

    let t: Tendencia = 'ESTAVEL';
    const diff = fAtual - fAnterior;
    if (diff >= 10) t = 'MELHORANDO';
    else if (diff <= -10) t = 'PIORANDO';

    return {
      freqAtual: fAtual,
      freqAnterior: fAnterior,
      totalAulas: totalRecente + totalAnterior,
      tendencia: t,
    };
  }, [alunoId, chamadasRecente, inicioRecenteStr, fimRecente, inicioAnteriorStr, fimAnteriorStr]);

  return { tendencia, freqAtual, freqAnterior, totalAulas, isLoading: l1 };
}
